import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      if (data?.authenticated) {
        const u = {
          userId: data.user_id,
          email: data.email,
          name: data.name,
          picture: data.picture,
          provider: data.provider,
          emailVerified: !!data.email_verified,
          role: data.role,
          accountStatus: data.account_status,
          planName: data.plan_name,
          isAdmin: !!data.is_admin,
          alpacaConnected: data.alpaca_connected,
          alpacaEnv: data.alpaca_env,
          alpacaConnectedAt: data.alpaca_connected_at,
          settings: data.settings || {
            signal_alerts_enabled: true,
            paper_auto_trading: false,
            stocks_enabled: true,
            options_enabled: false,
          },
        };
        setUser(u);
        return u;
      }
      setUser(null);
      return null;
    } catch (error) {
      console.error('[auth] refresh failed', error);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('[auth] logout request failed', error);
    }
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, logout, refresh }),
    [user, loading, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
